const normalizeUrl = (url) => {
  if (!url) return url;
  return url.endsWith('/') ? url.slice(0, -1) : url;
};

// Automatically detect environment and set API URL
const getApiBaseUrl = () => {
  // FORCE localhost in development mode, regardless of REACT_APP_API_URL or hostname
  // This ensures that when developing locally, we always use localhost server
  if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5000';
  }
  
  // In production, check if REACT_APP_API_URL is explicitly set
  if (process.env.REACT_APP_API_URL) {
    return normalizeUrl(process.env.REACT_APP_API_URL);
  }
  
  // Check if we're in production (deployed on Netlify, Heroku or production domain)
  const isProduction = 
    process.env.NODE_ENV === 'production' ||
    window.location.hostname.includes('netlify.app') ||
    window.location.hostname.includes('herokuapp.com') ||
    (window.location.hostname.includes('spintasker') && !window.location.hostname.includes('localhost'));
  
  if (isProduction) {
    // Production: Use REACT_APP_API_URL if set, otherwise use Heroku URL as fallback
    // This allows Netlify to override via environment variable
    return process.env.REACT_APP_API_URL || 'https://spintasker-7dacb9bfc5d5.herokuapp.com';
  } else {
    // Default: Use localhost
    return 'http://localhost:5000';
  }
};

export const API_BASE_URL = getApiBaseUrl();
export const SOCKET_BASE_URL = API_BASE_URL;

// Google Identity Services Client ID
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Google Analytics Measurement ID
export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

// Log Google Client ID status (only in development)
if (process.env.NODE_ENV === 'development') {
  if (GOOGLE_CLIENT_ID) {
    console.log('✅ Google Client ID loaded:', GOOGLE_CLIENT_ID.substring(0, 20) + '...');
  } else {
    console.warn('⚠️ Google Client ID not found in environment variables. Add REACT_APP_GOOGLE_CLIENT_ID to .env file');
  }
  
  if (GA_MEASUREMENT_ID) {
    console.log('✅ Google Analytics Measurement ID loaded:', GA_MEASUREMENT_ID);
  } else {
    console.warn('⚠️ Google Analytics Measurement ID not found. Add REACT_APP_GA_MEASUREMENT_ID to .env file (optional)');
  }
}

/**
 * Ekstraktuje company slug iz URL-a
 * @returns {string|null} - Company slug ili null ako nije pronađen
 */
/**
 * Lista rezervisanih reči koje ne mogu biti slug-ovi
 * Mora biti sinhronizovano sa server/utils/slugGenerator.js
 */
const RESERVED_WORDS = [
  'auth',
  'verify-email',
  'forgot-password',
  'reset-password',
  'superadmin',
  'dashboard',
  'api',
  'static',
  'assets',
  'images',
  'admin',
  'login',
  'register',
  'signup',
  'logout',
  'profile',
  'settings',
  'help',
  'support',
  'contact',
  'about',
  'terms',
  'privacy',
  'legal',
  'blog',
  'news',
  'docs',
  'documentation',
  'status',
  'health',
  'ping',
  'favicon.ico',
  'robots.txt',
  'sitemap.xml',
  'fakturisanje'
];

export const getCompanySlug = () => {
  // Prvo proveri URL parametar (npr. /moj-servis/dashboard)
  const pathname = window.location.pathname;
  const pathParts = pathname.split('/').filter(Boolean); // Ukloni prazne stringove
  
  // Ako postoji prvi segment i nije rezervisana ruta, to je verovatno company slug
  if (pathParts.length > 0) {
    const potentialSlug = pathParts[0].toLowerCase();
    
    // Proveri da li je rezervisana reč
    if (RESERVED_WORDS.includes(potentialSlug)) {
      // Ako je rezervisana reč, ne koristi je kao slug
      return localStorage.getItem('currentCompanySlug');
    }
    
    // Proveri da li izgleda kao slug (samo mala slova, brojevi i crtice)
    if (/^[a-z0-9-]+$/.test(potentialSlug)) {
      return potentialSlug;
    }
  }
  
  // Fallback na localStorage
  return localStorage.getItem('currentCompanySlug');
};

/**
 * Generiše API URL sa company slug-om ako je dostupan
 * @param {string} endpoint - API endpoint (npr. '/api/jobs')
 * @returns {string} - Kompletan API URL sa slug-om ako je potrebno
 */
export const getApiUrl = (endpoint) => {
  const baseUrl = API_BASE_URL;
  const slug = getCompanySlug();
  
  // Ako imamo slug i endpoint počinje sa /api/, dodaj slug
  if (slug && endpoint && endpoint.startsWith('/api/')) {
    // Zameni /api/ sa /api/:slug/
    const sluggedEndpoint = endpoint.replace('/api/', `/api/${slug}/`);
    return `${baseUrl}${sluggedEndpoint}`;
  }
  
  // Inače, vrati standardni URL
  return `${baseUrl}${endpoint}`;
};

export default {
  API_BASE_URL,
  SOCKET_BASE_URL,
  getCompanySlug,
  getApiUrl,
};

